import React from 'react';
import { Header, Breadcrumb, PaymentCard, SupportCard, ST, countryName, flagEmoji } from './Chrome';
import { SiteFooterV2 } from './Footers';
import { SLUG_BY_ISO } from '../../lib/countrySlugs';

const DEFAULT_HERO = `${ST}/images/2/bg-temple.jpg`;

///////////////////////////////////////////////////////////////////////////////
// Trust strip — sits directly under the hero entry on a country landing page.
// Every competitor puts its trust signals above the fold (CellPay, Recharge and
// TopUp all do); ours were buried in the rail, below the catalogue.
///////////////////////////////////////////////////////////////////////////////
const TRUST_POINTS = [
  { k: 'bolt', t: 'Credit lands in seconds' },
  { k: 'lock', t: 'Secure checkout, 3-D Secure' },
  { k: 'clock', t: 'Support 24/7' },
];

const TRUST_ICON = {
  bolt: 'M13 2 3 14h8l-1 8 10-12h-8l1-8z',
  lock: 'M3 11h18v10H3zM7 11V7a5 5 0 0 1 10 0v4',
  clock: 'M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18zM12 7v5l3 2',
  check: 'M20 6 9 17l-5-5',
};

export function TrustStrip() {
  return (
    <div className="truststrip">
      {TRUST_POINTS.map((p) => (
        <span key={p.k}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"
            strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d={TRUST_ICON[p.k]} />
          </svg>
          {p.t}
        </span>
      ))}
    </div>
  );
}

///////////////////////////////////////////////////////////////////////////////
// Proof figures. Only claims we can stand behind — no invented star rating.
///////////////////////////////////////////////////////////////////////////////
///////////////////////////////////////////////////////////////////////////////
// Landing-page copy + shared SEO sections.
//
// The country landing pages themselves are NOT separate pages: /topup/<slug>
// renders RechargePage and /esim/<slug> renders the eSIM home, each preset to
// the country and wrapped with the sections below — so every change to the
// main pages reaches all 159 country pages automatically. This module only
// owns the words and the region hubs.
///////////////////////////////////////////////////////////////////////////////

// Hand-written copy for the focus markets; every other country falls back to
// genericTopupCopy(). Keyed by ISO.
export const TOPUP_COPY = {
  US: {
    h1: 'USA prepaid refills, delivered in seconds',
    sub: 'Top up AT&T, T-Mobile, Verizon, Boost, Cricket, Metro and every major prepaid carrier — pay online, credit lands instantly.',
    faq: [
      ['How fast is a USA phone refill?', 'Refills are delivered directly to the phone number within seconds of payment. No PIN codes to dial in for RTR carriers — the credit is applied automatically.'],
      ['Which US carriers can I top up?', 'AT&T, T-Mobile, Verizon, Boost Mobile, Cricket, Metro by T-Mobile, Simple Mobile, Straight Talk, Tracfone, H2O, Lyca Mobile, Ultra Mobile, Total Wireless, Page Plus, NET10 and more.'],
      ['Can I refill someone else’s phone?', 'Yes — enter any US prepaid number. It’s a popular way to send airtime to family. You only need their phone number.'],
      ['What payment methods do you accept?', 'International credit and debit cards (with 3-D verification), USDC, and your MobileTopup balance.'],
    ],
    seo: 'MobileTopup.com is an authorized reseller of prepaid refills for all major US networks. Whether you’re refilling your own prepaid plan or sending credit to family in the United States, your top-up is delivered straight to the phone — no codes, no store visit, available 24/7.',
  },
  GB: {
    h1: 'UK mobile top-up online',
    sub: 'EE, O2, Vodafone, Three, giffgaff, Lebara, Lyca and more — instant top-up vouchers and refills for every UK network.',
    faq: [
      ['Which UK networks can I top up?', 'EE, O2, Vodafone, Three, giffgaff, Lebara, Lyca Mobile, Tesco Mobile, ASDA Mobile, Talk Home and more.'],
      ['How does a UK top-up voucher work?', 'For PIN-based networks you receive a top-up code instantly by email; apply it from your handset (usually by dialling a short code like 150). RTR networks are credited automatically.'],
      ['Can I top up a UK number from abroad?', 'Yes — that’s what we’re for. Pay with any international card and the credit reaches the UK number in seconds.'],
    ],
    seo: 'Send mobile credit to any UK prepaid phone from anywhere in the world. MobileTopup.com delivers EE, O2, Vodafone and Three top-ups instantly, 24/7, with no UK bank account or store visit needed.',
  },
  TH: {
    h1: 'Thailand mobile topup — AIS, DTAC, TrueMove H',
    sub: 'Enter the number, we detect the carrier, credit arrives in seconds. The same one-step topup thousands of customers have used since 2014.',
    faq: [
      ['Which Thai carriers are supported?', 'AIS (1-2-Call), DTAC (Happy) and TrueMove H prepaid numbers, plus NT/my.'],
      ['How fast does the credit arrive?', 'Within seconds. Our Thai topups are delivered over a direct carrier connection, around the clock.'],
      ['Do you support Thai promotions and packages?', 'Yes — net packages and validity topups are available for supported carriers at checkout.'],
    ],
    seo: 'MobileTopup.com has delivered instant AIS, DTAC and TrueMove H topups since 2014. Enter any Thai mobile number, pay online, and the credit is on the phone before you close the tab.',
  },
  PH: {
    h1: 'Philippines load online — Globe, Smart, TM, TNT, DITO',
    sub: 'Send prepaid load home in seconds. Regular load and promo-ready credit for every Philippine network.',
    faq: [
      ['Which Philippine networks can I load?', 'Globe, Smart, TM (Touch Mobile), TNT (Talk N Text), Sun Cellular, DITO, Cherry Prepaid and GOMO.'],
      ['How fast does the load arrive?', 'Load is credited to the number within seconds of payment — the recipient gets the standard carrier SMS confirmation.'],
      ['Can the recipient use the load for promos?', 'Yes. Regular load works for GIGA, UNLI and other carrier promos — the recipient registers the promo as usual.'],
    ],
    seo: 'The fastest way to send load to the Philippines. MobileTopup.com delivers Globe, Smart, TM, TNT and DITO prepaid load instantly to any number — perfect for keeping family connected back home.',
  },
};

export function genericTopupCopy(name) {
  return {
    sub: `Instant prepaid credit for every supported carrier in ${name} — pick an amount, enter the number, done.`,
    faq: [
      [`How do I top up a phone in ${name}?`, 'Pick the carrier and amount below, enter the phone number, and pay — the credit is delivered directly to the phone, usually within seconds.'],
      ['Do I need an account?', 'No. Checkout is guest-friendly: an email address for your receipt is all we need.'],
      ['What payment methods do you accept?', 'International credit and debit cards, USDC, and your MobileTopup balance.'],
      [`Can I send credit to someone else in ${name}?`, 'Yes — enter their number instead of yours. Topping up family and friends abroad is what most of our customers do.'],
    ],
    seo: `MobileTopup.com delivers instant prepaid mobile topup to ${name}. All amounts are shown up front in USD with the local receive value, credit is sent straight to the phone after payment, and our support agents are available around the clock.`,
  };
}

export function esimCopy(name) {
  return {
    sub: `Prepaid eSIM data plans for ${name} — instant delivery, install by QR, no physical SIM.`,
    faq: [
      [`How does a ${name} eSIM work?`, 'Buy a plan, scan the QR code we send you, and data works the moment you arrive — no SIM swap, no roaming charges from your home carrier.'],
      ['When does the plan start?', 'Most plans activate on first connection to a local network, so you can install before you travel.'],
      ['Is my phone compatible?', 'Most phones from 2019 onward support eSIM (iPhone XS and later, recent Samsung, Google Pixel). Your phone must be carrier-unlocked.'],
      ['What if I don’t use the eSIM?', 'Unused eSIMs with zero data used qualify for automatic refunds.'],
    ],
    seo: `Compare prepaid eSIM data plans for ${name} by price per GB. MobileTopup.com delivers the QR code instantly after payment — install in minutes, connect on arrival, and top up the same eSIM later if you need more data.`,
  };
}

export const TOPUP_STEPS = [
  ['1', 'Choose your carrier'],
  ['2', 'Pick an amount'],
  ['3', 'Enter the phone number'],
  ['4', 'Pay — credit lands in seconds'],
];

export const ESIM_STEPS = [
  ['1', 'Pick a destination plan'],
  ['2', 'Pay — QR arrives instantly'],
  ['3', 'Scan to install'],
  ['4', 'Connect on arrival'],
];

export function StepsStrip({ steps = TOPUP_STEPS }) {
  return (
    <div className="steps-strip">
      {steps.map(([n, label]) => (
        <div className="step" key={n}><span className="n">{n}</span>{label}</div>
      ))}
    </div>
  );
}

// FAQ + SEO copy block rendered under the main catalog on landing pages.
export function LandingSections({ faq, seo }) {
  return (
    <>
      {faq && faq.length > 0 && (
        <section className="faq">
          <h2 className="landing-h2">Frequently asked questions</h2>
          {faq.map(([q, a]) => (
            <details key={q}><summary>{q}</summary><p>{a}</p></details>
          ))}
        </section>
      )}
      {seo && <p className="seo-copy">{seo}</p>}
    </>
  );
}

///////////////////////////////////////////////////////////////////////////////
// Region hubs: /topup/asia and /topup/north-america
///////////////////////////////////////////////////////////////////////////////

export const REGIONS = {
  asia: {
    name: 'Asia',
    flag: '🌏',
    h1: 'Mobile topup across Asia',
    sub: 'Instant prepaid credit for phones in Thailand, the Philippines, Indonesia, India, Vietnam and across the region.',
    countries: ['TH', 'PH', 'ID', 'IN', 'VN', 'MM', 'KH', 'LA', 'MY', 'SG', 'JP', 'BD', 'PK', 'NP', 'LK', 'CN'],
    seo: 'One checkout for prepaid mobile credit across Asia. Pick a country to see its carriers and denominations — credit is delivered directly to the phone in seconds.',
  },
  'north-america': {
    name: 'North America',
    flag: '🌎',
    h1: 'Mobile topup in North America',
    sub: 'Prepaid refills for the USA, Canada and Mexico — AT&T, T-Mobile, Telcel, Rogers and every major prepaid brand.',
    countries: ['US', 'CA', 'MX', 'DO', 'GT', 'SV', 'HN', 'JM', 'HT', 'CU'],
    seo: 'Refill any prepaid phone in the USA, Canada, Mexico, the Caribbean and Central America. Instant delivery, every major carrier, cards and USDC accepted.',
  },
};

export function RegionLanding({ region, me, currency, onCurrency }) {
  return (
    <div className="page">
      <Header me={me} active="recharge" currency={currency} onCurrency={onCurrency} />
      <section className="hero landing-hero" style={{ backgroundImage: `linear-gradient(rgba(48,164,174,.85), rgba(30,147,157,.93)), url(${DEFAULT_HERO})` }}>
        <Breadcrumb active="recharge" />
        <h1 className="hero-title">{region.flag} {region.h1}</h1>
        <p className="hero-sub">{region.sub}</p>
      </section>
      <div className="body">
        <main className="main landing-main">
          <h2 className="landing-h2">Pick a country</h2>
          <div className="country-grid">
            {region.countries.map((iso) => {
              const slug = SLUG_BY_ISO[iso];
              const href = slug ? `/topup/${slug}` : `/recharge?country=${iso}`;
              return (
                <a className="country-tile" key={iso} href={href}>
                  <span className="flag">{flagEmoji(iso)}</span>
                  <span className="cname">{countryName(iso)}</span>
                </a>
              );
            })}
          </div>
          <p className="seo-copy">{region.seo}</p>
        </main>
        <aside className="rail">
          <PaymentCard currency={currency} />
          <SupportCard />
        </aside>
      </div>
      <SiteFooterV2 currency={currency} onCurrency={onCurrency} />
    </div>
  );
}
